/**
 * The gaze feature extractor of Miłosz's engine, in the browser: one camera
 * frame + MediaPipe landmarks in, five numbers out.
 *
 * Port of eye-tracking-sandbox v0.1.0 — the release the ECS gaze sidecar runs
 * (extractors/cnn_onnx.py + vision/{camera,head_pose,normalization}.py):
 *
 *   landmarks → PnP head pose → virtual-camera homography → 448² uint8 warp →
 *   model.onnx (resize to 224², ImageNet normalisation, ResNet-18) → (pitch, yaw)
 *
 * and returns `[pitch, yaw, head_axis_x, head_axis_y, head_axis_z]`, the
 * container's feature row. The model file is the release asset itself
 * (sha256 1b4acead…), not a re-export, so everything from the 448² crop onward
 * is literally the container's code path.
 */
import { CROP_SIZE, FaceWarper } from './faceWarp'
import {
  approxCameraMatrix,
  DEFAULT_FOV_DEG,
  FACE_LANDMARK_IDS,
  normalizationMatrices,
  solveHeadPose,
} from './headGeometry'
import type { Mat3 } from './linalg'

/**
 * Above this mean reprojection error the pose solve did not explain the
 * landmarks and the crop points somewhere other than the face. Generous on
 * purpose — real frames sit far below it.
 */
const MAX_REPROJECTION_PX = 12

export interface ExtractorStatus {
  /** Rolling mean of the ONNX forward pass alone, ms. */
  inferenceMs: number
}

export interface ExtractedFrame {
  /** [pitch, yaw, hx, hy, hz] — the calibrator's input, as in the container. */
  features: number[]
  reprojectionPx: number
  distanceMm: number
}

export class CnnExtractor {
  private worker: Worker | null = null
  private pending = new Map<number, (angles: number[] | null) => void>()
  private nextId = 0
  private warper: FaceWarper | null = null
  private input = new Uint8Array(3 * CROP_SIZE * CROP_SIZE)
  private inferenceMs = 0
  private busy = false
  private disposed = false
  private camera: { K: Mat3; w: number; h: number } | null = null

  readonly fovDeg: number

  constructor(fovDeg = DEFAULT_FOV_DEG) {
    this.fovDeg = fovDeg
  }

  /** Hand the model to the inference worker (cnnWorker.ts) and wait until it is loaded. */
  async init(model: Uint8Array): Promise<void> {
    const worker = new Worker(new URL('./cnnWorker.ts', import.meta.url), { type: 'module' })
    await new Promise<void>((resolve, reject) => {
      worker.onmessage = (event) => {
        const msg = event.data
        if (msg.type === 'ready') resolve()
        else if (msg.type === 'error') reject(new Error(`gaze model: ${msg.message}`))
        else if (msg.type === 'result') {
          this.pending.get(msg.id)?.(msg.angles)
          this.pending.delete(msg.id)
        }
      }
      worker.onerror = (event) => reject(new Error(`gaze model worker: ${event.message}`))
      worker.postMessage({ type: 'init', model }, [model.buffer])
    })
    if (this.disposed) {
      worker.terminate()
      return
    }
    this.worker = worker
    this.warper = new FaceWarper()
  }

  private run(crop: Uint8Array): Promise<number[] | null> {
    const worker = this.worker
    if (!worker) return Promise.resolve(null)
    const id = this.nextId++
    return new Promise((resolve) => {
      this.pending.set(id, resolve)
      // Copy, then transfer the copy: the warper reuses `this.input` next frame.
      const copy = crop.slice()
      worker.postMessage({ type: 'run', id, crop: copy }, [copy.buffer])
    })
  }

  get ready(): boolean {
    return this.worker !== null && this.warper !== null
  }

  /** True while a forward pass is in flight — callers skip frames instead of queueing them. */
  get isBusy(): boolean {
    return this.busy
  }

  status(): ExtractorStatus {
    return { inferenceMs: this.inferenceMs }
  }

  /**
   * Run one frame. `landmarks` are MediaPipe's normalised coordinates for the
   * whole face. The source must be the UNMIRRORED frame — a CSS flip on the
   * element is fine, drawing a flipped frame would invert yaw and head_axis_x.
   */
  async extract(
    source: TexImageSource,
    width: number,
    height: number,
    landmarks: ArrayLike<{ x: number; y: number }>,
  ): Promise<ExtractedFrame | null> {
    const warper = this.warper
    if (!this.worker || !warper || this.disposed || this.busy) return null
    if (width <= 0 || height <= 0) return null
    if (!this.camera || this.camera.w !== width || this.camera.h !== height) {
      this.camera = { K: approxCameraMatrix(width, height, this.fovDeg), w: width, h: height }
    }

    const points: Array<[number, number]> = []
    for (const id of FACE_LANDMARK_IDS) {
      const lm = landmarks[id]
      if (!lm) return null
      points.push([lm.x * width, lm.y * height])
    }
    const pose = solveHeadPose(points, this.camera.K)
    if (!pose || pose.reprojectionPx > MAX_REPROJECTION_PX) return null
    const norm = normalizationMatrices(this.camera.K, pose.R, pose.faceCenter)
    if (!norm) return null
    if (!warper.warp(source, width, height, norm.W, this.input)) return null

    this.busy = true
    const started = performance.now()
    let angles: number[] | null
    try {
      angles = await this.run(this.input)
    } finally {
      this.busy = false
    }
    if (!angles) return null
    const elapsed = performance.now() - started
    this.inferenceMs = this.inferenceMs === 0 ? elapsed : this.inferenceMs * 0.9 + elapsed * 0.1

    const pitch = angles?.[0]
    const yaw = angles?.[1]
    if (!Number.isFinite(pitch) || !Number.isFinite(yaw)) return null
    return {
      features: [pitch, yaw, norm.headAxis[0], norm.headAxis[1], norm.headAxis[2]],
      reprojectionPx: pose.reprojectionPx,
      distanceMm: norm.distanceMm,
    }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.warper?.dispose()
    this.warper = null
    this.worker?.terminate()
    this.worker = null
    for (const resolve of this.pending.values()) resolve(null)
    this.pending.clear()
  }
}
